// Must match cms/server/config/middlewares.ts (formidable.maxFileSize, a
// per-request total -- see uploadFilesSequentially) and
// cms/server/config/plugins.ts (sizeLimit)
export const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10 MB

export function validateFileSize(file: File): void {
  if (file.size > MAX_UPLOAD_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(`File "${file.name}" is ${sizeMB} MB which exceeds the 10 MB limit`);
  }
}

/**
 * Extracts a human-readable reason from a failed /upload response instead of
 * discarding it. Strapi's error envelope (`{error: {message}}`) is forwarded
 * verbatim by the BFF proxy, so this mirrors the `errorData.error?.message`
 * pattern already used for entity-create/update calls in the same stores.
 */
export async function getUploadErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body: { error?: { message?: string } } = await response.json();
    if (body?.error?.message) {
      return body.error.message;
    }
  } catch { // eslint-disable-line no-restricted-syntax -- non-JSON error body falls back below
    // response body wasn't JSON (e.g. a gateway error page) — use the fallback
  }
  return fallback;
}

/**
 * Uploads each file in its own POST to the BFF upload proxy, in order, and
 * returns the new media ids in the same order as `files`.
 *
 * One file per request is deliberate (GH #1261). Strapi's body parser
 * (koa-body 6, which bundles formidable 2) checks `formidable.maxFileSize`
 * against the running total of every file in a request, not against each
 * file, so several photos that were each under the cap were rejected together
 * with a 413. A request holding one file is held to the per-file limit only.
 *
 * Every file is size-checked before anything is sent, so an oversized file
 * never strands the files ahead of it. A failed request stops the loop: later
 * files are not sent, and the server's message (or `fallbackMessage`) is
 * thrown. Files that finished uploading before the failure stay in the media
 * library, unattached -- the same outcome as a save that fails after its
 * upload succeeded.
 */
export async function uploadFilesSequentially(files: File[], fallbackMessage: string): Promise<number[]> {
  files.forEach((file) => validateFileSize(file));

  const ids: number[] = [];
  for (const file of files) {
    const formData = new FormData();
    formData.append('files', file);

    const response = await fetch('/api/strapi/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(await getUploadErrorMessage(response, fallbackMessage));
    }

    const uploaded: Array<{ id?: unknown }> = await response.json();
    const id = uploaded[0]?.id;
    if (typeof id !== 'number') {
      throw new Error(fallbackMessage);
    }
    ids.push(id);
  }
  return ids;
}
